import ProductsApiV2 from 'api/v2/admins/products-api-v2'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { Alert, Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'

const formatSentAt = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('id-ID')
}

// Shared between Inventory List and Detail, matching the legacy Trestle modal.
const SendPhotoLinkEmailModal = ({ isOpen, product, onClose, onSent }) => {
  const [ link, setLink ] = useState('')
  const [ recipient, setRecipient ] = useState('')
  const [ forceResend, setForceResend ] = useState(false)
  const [ isSending, setIsSending ] = useState(false)
  const [ errorMessage, setErrorMessage ] = useState('')
  const alreadySent = product?.photo_link_email_sent === true

  useEffect(() => {
    if (!isOpen) return
    setLink(product?.photo_folder_link || '')
    setRecipient(product?.consignor_email || '')
    setForceResend(false)
    setErrorMessage('')
  }, [ isOpen, product ])

  const close = () => {
    if (!isSending) onClose()
  }

  const submit = async (event) => {
    event.preventDefault()
    if (!product?.id || isSending) return

    if (!link.trim()) {
      setErrorMessage('Photo folder link wajib diisi.')
      return
    }
    if (!recipient.trim()) {
      setErrorMessage('Consignor email wajib diisi.')
      return
    }
    if (alreadySent && !forceResend) {
      setErrorMessage('Email photo link sudah pernah dikirim. Centang checklist kirim ulang jika memang ingin resend.')
      return
    }

    setIsSending(true)
    setErrorMessage('')
    try {
      await ProductsApiV2.sendPhotoLinkEmail({
        id      : product.id,
        payload : {
          photo_folder_link : link.trim(),
          consignor_email   : recipient.trim(),
          force_resend      : forceResend
        }
      })
      onSent()
      onClose()
    }
    catch (error) {
      setErrorMessage(error.response?.data?.messages?.base || error.response?.data?.message || 'Failed to send photo link email.')
    }
    finally {
      setIsSending(false)
    }
  }

  return (
    <Modal isOpen={ isOpen } toggle={ close } centered>
      <Form onSubmit={ submit }>
        <ModalHeader toggle={ isSending ? undefined : close }>Input Photo Link and Send Email</ModalHeader>
        <ModalBody>
          <div className='mb-3 small text-muted'>SKU: { product?.sku || '-' }</div>
          <FormGroup>
            <Label for='photo-link-email-url'><strong>Photo Folder Link</strong></Label>
            <Input id='photo-link-email-url' type='text' value={ link } disabled={ isSending } onChange={ (event) => setLink(event.target.value) } />
          </FormGroup>
          <FormGroup>
            <Label for='photo-link-email-recipient'><strong>Consignor Email</strong></Label>
            <Input id='photo-link-email-recipient' type='text' value={ recipient } disabled={ isSending } onChange={ (event) => setRecipient(event.target.value) } />
          </FormGroup>
          <hr />
          <div className='font-weight-bold mb-2'>Status Pengiriman Email</div>
          <FormGroup check className='mb-2'>
            <Label check>
              <Input type='checkbox' checked={ alreadySent } readOnly disabled />
              {' '}Email photo link sudah pernah dikirim
            </Label>
          </FormGroup>
          { alreadySent && (
            <>
              <Alert color='danger' className='py-2'>
                <div><strong>Terakhir dikirim:</strong> { formatSentAt(product.photo_link_email_sent_at) }</div>
                { product.photo_link_email_sent_by && <div><strong>Oleh:</strong> { product.photo_link_email_sent_by }</div> }
              </Alert>
              <FormGroup check className='mb-3'>
                <Label check>
                  <Input type='checkbox' checked={ forceResend } disabled={ isSending } onChange={ (event) => setForceResend(event.target.checked) } />
                  {' '}Saya yakin, kirim ulang email ini
                </Label>
              </FormGroup>
            </>
          ) }
          <p className='mb-2 font-weight-bold'>Jangan lupa untuk memberikan akses link ke email yang dituju.</p>
          { errorMessage && <Alert color='danger' className='mb-0'>{ errorMessage }</Alert> }
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' type='button' onClick={ close } disabled={ isSending }>Cancel</Button>
          <Button color='primary' type='submit' disabled={ isSending || !product?.id || (alreadySent && !forceResend) }>
            { isSending ? 'Sending...' : 'Submit' }
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

SendPhotoLinkEmailModal.propTypes = {
  isOpen  : PropTypes.bool,
  product : PropTypes.object,
  onClose : PropTypes.func.isRequired,
  onSent  : PropTypes.func.isRequired
}

export default SendPhotoLinkEmailModal
